import {EventInput} from "@fullcalendar/vue3";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import Translator from "@/utils/common/Translator.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import {QTreeNode} from "quasar";
import TechnicianAppointmentUtils, {
  AppointmentCondition, CalendarDateRange, FilterRule
} from "@/utils/common/TechnicianAppointmentUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import AppointmentsRole from "@/utils/constants/AppointmentsRole.ts";

export interface AppointmentFilters {
  fromDate?: any;
  toDate?: any;
  conditions: AppointmentCondition[];
  rules: FilterRule[];
  matchAny: boolean;
}

export interface FilterRow {
  uid: number;
  key: string;
  negate: boolean;
}

export const TECHNICIAN_FILTER_KEY = "technician";

export default class MyAppointmentsUtils {

  static async resolveScope(): Promise<Required<namaTypes.DTOMyAppointmentsScope> | null> {
    const scope = await TechnicianAppointmentUtils.fetchMyAppointmentsScope();
    if (!scope?.role || scope.role === AppointmentsRole.None)
      return null;
    return {role: scope.role, crews: scope.crews ?? []};
  }

  private static readonly RULE_PATHS: Record<string, string> = {
    customer: "customer.id",
    branch: "branch.id",
    region: "customerAddress.region.id",
    technicianProcedure: "technicianProcedure.id",
    departmentSection: "departmentSection.id",
    fromDoc: "fromDoc.id",
    technicianAppointment: "id"
  };

  static rowFieldId(row: FilterRow): string {
    return "rule" + row.uid;
  }

  static readFilters(record: any, statuses: string[], rows: FilterRow[], matchAny: boolean, crews: namaTypes.DTOTechnicianCrewInfo[]): AppointmentFilters {
    const rules: FilterRule[] = rows.flatMap<FilterRule>(row => {
      const id = record?.[MyAppointmentsUtils.rowFieldId(row)]?.id as string | undefined;
      if (!id)
        return [];
      if (row.key === TECHNICIAN_FILTER_KEY)
        return [{field: "details.technicianCrew.id", negate: row.negate, value: MyAppointmentsUtils.crewIdsWithMember(crews, id)}];
      return [{field: MyAppointmentsUtils.RULE_PATHS[row.key], negate: row.negate, value: id}];
    });
    const conditions: AppointmentCondition[] = statuses.length ? [["status", statuses]] : [];
    return {fromDate: record?.fromDate, toDate: record?.toDate, conditions, rules, matchAny};
  }

  // ponytail: "is not technician" keeps appointments with any period on a crew without him; tighten if multi-crew visits need it
  private static crewIdsWithMember(crews: namaTypes.DTOTechnicianCrewInfo[], employeeId: string): string[] {
    const ids = crews.filter(info => info.memberIds?.includes(employeeId)).map(info => info.crew?.id as string);
    return ids.length ? ids : ["-"];
  }

  static hasFilterValue(value: any): boolean {
    if (value && typeof value === "object" && !(value instanceof Date))
      return !!value.id;
    return ObjectChecker.isNotEmptyOrNull(value);
  }

  static crewIds(crews: namaTypes.DTOTechnicianCrewInfo[]): string[] {
    return crews.map(info => info.crew?.id).filter(Boolean) as string[];
  }

  static sectionIds(crews: namaTypes.DTOTechnicianCrewInfo[]): string[] {
    return [...new Set(crews.map(info => info.departmentSection?.id).filter(Boolean))] as string[];
  }

  static technicianIds(crews: namaTypes.DTOTechnicianCrewInfo[]): string[] {
    return [...new Set(crews.flatMap(info => info.memberIds ?? []))];
  }

  static crewTree(crews: namaTypes.DTOTechnicianCrewInfo[]): QTreeNode[] {
    const sections = new Map<string, QTreeNode>();
    for (const {crew, departmentSection: section} of crews) {
      const key = "section:" + (section?.id ?? "");
      if (!sections.has(key))
        sections.set(key, {
          id: key,
          label: section?.id ? TechnicianAppointmentUtils.refLabel(section) : Translator.translate("myAppointmentsNoSection"),
          children: []
        });
      sections.get(key)!.children!.push({id: crew?.id, label: TechnicianAppointmentUtils.refLabel(crew), crew});
    }
    const byLabel = (a: QTreeNode, b: QTreeNode) => String(a.label ?? "").localeCompare(String(b.label ?? ""));
    const roots = [...sections.values()].sort(byLabel);
    roots.forEach(root => root.children!.sort(byLabel));
    return roots;
  }

  static rangeWithin(range: CalendarDateRange, fromDate?: any, toDate?: any): CalendarDateRange | null {
    let start = dayjs(range.start);
    let end = dayjs(range.end);
    const from = MyAppointmentsUtils.filterDay(fromDate);
    const dayAfterToDate = MyAppointmentsUtils.filterDay(toDate)?.add(1, "day");
    if (from?.isAfter(start))
      start = from;
    if (dayAfterToDate?.isBefore(end))
      end = dayAfterToDate;
    return start.isBefore(end) ? {start: start.toDate(), end: end.toDate()} : null;
  }

  static periodTarget(range: CalendarDateRange | null, fromDate?: any, toDate?: any): Date | null {
    const from = MyAppointmentsUtils.filterDay(fromDate);
    const dayAfterToDate = MyAppointmentsUtils.filterDay(toDate)?.add(1, "day");
    if (!from && !dayAfterToDate)
      return null;
    // an inverted period matches nothing anywhere, so jumping would only strand the user on an empty week
    if (from && dayAfterToDate && !from.isBefore(dayAfterToDate))
      return null;
    if (range) {
      const overlapsStart = !dayAfterToDate || dayAfterToDate.isAfter(dayjs(range.start));
      const overlapsEnd = !from || from.isBefore(dayjs(range.end));
      if (overlapsStart && overlapsEnd)
        return null;
    }
    return (from ?? dayAfterToDate!.subtract(1, "day")).toDate();
  }

  private static filterDay(value: any) {
    if (!value)
      return null;
    const parsed = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }

  static viewPlaceFor(appointmentId?: string): EditPlace {
    return EditPlace.createInstance(TechnicianAppointmentUtils.APPOINTMENT_ENTITY, appointmentId, ViewMode.VIEW);
  }

  static viewLinkFor(appointmentId?: string): string {
    return PlaceUtils.linkToPlace(MyAppointmentsUtils.viewPlaceFor(appointmentId));
  }

  static buildScheduleEvents(periods: namaTypes.DTOCrewPeriod[], colorMap: Map<string, string>): EventInput[] {
    return periods.map(period => {
      const event = TechnicianAppointmentUtils.busyEventFor(period, colorMap);
      return {
        ...event,
        editable: false,
        url: MyAppointmentsUtils.viewLinkFor(period.appointment?.id),
        extendedProps: {...event.extendedProps, readonly: true}
      } as EventInput;
    });
  }

  static dayKey(date: any): string {
    const parsed = date == null ? null : dayjs(date);
    return parsed?.isValid() ? parsed.format("YYYY-MM-DD") : "";
  }
}
